import { StandardEvents } from '@shopify/events';

const instances = new WeakMap();
const init = root => {
  if (instances.has(root)) return;
  const abort = new AbortController();
  const signal = abort.signal;
  instances.set(root, abort);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const gallery = root.querySelector('[data-hw-gallery]');
  const thumbs = [...root.querySelectorAll('[data-hw-thumb]')];
  const slides = [...root.querySelectorAll('[data-hw-slide]')];
  let active = 0;
  const fitGallery = () => {
    const image = gallery.querySelectorAll('.hw-gallery__image')[active];
    const width = Number(image?.getAttribute('width'));
    const height = Number(image?.getAttribute('height'));
    if (width > 0 && height > 0) gallery.style.setProperty('--hw-gallery-ratio', `${width} / ${height}`);
  };
  const updateGallery = () => {
    active = Math.max(0, Math.min(thumbs.length - 1, Math.round(gallery.scrollLeft / Math.max(1, gallery.clientWidth))));
    fitGallery();
    thumbs.forEach((button, i) => button.setAttribute('aria-pressed', String(i === active)));
  };
  const selectImage = (id, smooth = false) => {
    const i = slides.findIndex(slide => slide.dataset.imageId === String(id));
    if (i < 0) return;
    gallery.scrollTo({left: i * gallery.clientWidth, behavior: smooth && !reduced.matches ? 'smooth' : 'instant'});
    active = i;
    fitGallery();
    thumbs.forEach((button, index) => button.setAttribute('aria-pressed', String(index === i)));
  };
  thumbs.forEach((button, i) => button.addEventListener('click', () => selectImage(slides[i].dataset.imageId, true), {signal}));
  gallery.addEventListener('scroll', updateGallery, {passive:true, signal});
  gallery.addEventListener('keydown', event => {
    if (!['ArrowLeft','ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    const i = Math.max(0, Math.min(thumbs.length - 1, active + (event.key === 'ArrowRight' ? 1 : -1)));
    selectImage(slides[i].dataset.imageId, true);
  }, {signal});
  const dialog = root.querySelector('[data-hw-dialog]');
  const zoomImage = root.querySelector('[data-hw-zoom-image]');
  root.querySelector('[data-hw-zoom]').addEventListener('click', () => {
    const img = slides[active].querySelector('img');
    zoomImage.src = img.currentSrc || img.src;
    zoomImage.alt = img.alt;
    dialog.showModal();
  }, {signal});
  root.querySelector('[data-hw-close]').addEventListener('click', () => dialog.close(), {signal});
  dialog.addEventListener('click', event => {if (event.target === dialog) dialog.close();}, {signal});

  const primary = () => root.querySelector('.buy-buttons-block button[type="submit"]');
  const trust = root.querySelector('[data-hw-purchase-trust]');
  root.querySelector('.buy-buttons-block')?.insertAdjacentElement('afterend', trust);
  const stickyButton = root.querySelector('[data-hw-buy]');
  let changingVariant = false;
  const syncBuy = () => {stickyButton.disabled = changingVariant || !primary() || primary().disabled;};
  stickyButton.addEventListener('click', () => {
    if (!changingVariant && primary() && !primary().disabled) primary().click();
  }, {signal});
  const buttonObserver = new MutationObserver(syncBuy);
  const purchase = root.querySelector('.buy-buttons-block');
  if (purchase) buttonObserver.observe(purchase, {attributes:true, attributeFilter:['disabled'], childList:true, subtree:true});

  let generation = 0;
  root.addEventListener(StandardEvents.productSelect, event => {
    if (event.target.closest('product-card')) return;
    const currentGeneration = ++generation;
    changingVariant = true;
    syncBuy();
    event.promise.then(({detail}) => {
      if (currentGeneration !== generation || !root.isConnected || !detail?.html) return;
      const fresh = detail.html.querySelector('[data-hw-winder]');
      const stateElement = fresh?.querySelector('[data-hw-state]');
      if (!stateElement || fresh.dataset.hwModel !== root.dataset.hwModel) return;
      const state = JSON.parse(stateElement.textContent);
      selectImage(state.image, true);
      root.querySelector('[data-hw-sku]').textContent = state.sku || '';
      root.querySelector('[data-hw-price]').textContent = state.price;
      root.querySelector('[data-hw-variant]').textContent = state.variant;
      root.querySelector('[data-hw-state]').textContent = stateElement.textContent;
      for (const key of ['[data-hw-sticky] > img','[data-hw-buy-label]']) {
        const oldNode = root.querySelector(key), newNode = fresh.querySelector(key);
        if (oldNode && newNode) oldNode.replaceWith(newNode.cloneNode(true));
      }
      const currentCards = root.querySelector('[data-hw-card-count]');
      const freshCards = fresh.querySelector('[data-hw-card-count]');
      if (currentCards && freshCards) {
        currentCards.dataset.hwCardCount = freshCards.dataset.hwCardCount;
        currentCards.style.setProperty('--hw-card-count', freshCards.dataset.hwCardCount);
      }
      root.querySelectorAll('[data-hw-editorial]').forEach(container => {
        const replacement = fresh.querySelector(`[data-hw-editorial="${container.dataset.hwEditorial}"]`);
        if (replacement) container.hidden = replacement.hidden;
        const newImage = replacement?.querySelector('img');
        const currentImage = container.querySelector('img');
        if (currentImage && newImage && currentImage.getAttribute('src') !== newImage.getAttribute('src')) currentImage.replaceWith(newImage.cloneNode(true));
      });
    }).catch(error => {
      if (error.name !== 'AbortError') console.warn('Halloway: variant update could not complete.');
    }).finally(() => {
      if (currentGeneration === generation) {changingVariant = false; requestAnimationFrame(syncBuy);}
    });
  }, {signal});

  const stage = root.querySelector('[data-hw-scroll-scene]');
  const reading = root.querySelector('[data-hw-reading]');
  const sentence = reading.textContent.trim();
  reading.setAttribute('aria-label', sentence);
  reading.textContent = '';
  const words = sentence.split(/\s+/).map(word => {
    const span = document.createElement('span');
    span.textContent = word;
    span.className = 'hw-reading-word';
    span.setAttribute('aria-hidden','true');
    reading.append(span);
    return span;
  });
  let queued = false;
  const update = () => {
    queued = false;
    if (!root.isConnected) {abort.abort();buttonObserver.disconnect();return;}
    const box = stage.getBoundingClientRect();
    if (box.bottom > 0 && box.top < innerHeight) {
      const progress = Math.max(0, Math.min(1, (innerHeight * .62 - box.top) / Math.max(1, box.height - innerHeight * .3)));
      words.forEach((span, i) => span.style.setProperty('--hw-word-opacity', reduced.matches ? 1 : .3 + .7 * Math.max(0, Math.min(1, (progress - i / words.length) * words.length * 1.8))));
      stage.querySelector('img').style.setProperty('--hw-scale', reduced.matches ? 1 : 1.07 - .07 * progress);
    }
  };
  const requestUpdate = () => {if (!queued) {queued = true;requestAnimationFrame(update);}};
  document.addEventListener('scroll', requestUpdate, {passive:true, capture:true, signal});
  window.addEventListener('resize', () => {selectImage(slides[active].dataset.imageId);requestUpdate();}, {passive:true, signal});
  reduced.addEventListener('change', requestUpdate, {signal});
  if ('IntersectionObserver' in window && !reduced.matches) {
    root.classList.add('hw-enhanced');
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {entry.target.classList.add('hw-visible');observer.unobserve(entry.target);}
    }), {threshold:.08, rootMargin:'0px 0px -30px 0px'});
    root.querySelectorAll('[data-hw-reveal]').forEach(el => observer.observe(el));
    signal.addEventListener('abort', () => observer.disconnect(), {once:true});
  }
  selectImage(root.dataset.hwSelectedImage);
  syncBuy();
  update();
};
document.querySelectorAll('[data-hw-winder]').forEach(init);
document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-hw-winder]').forEach(init));
document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-hw-winder]').forEach(root => instances.get(root)?.abort()));

